import React, { useEffect } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { Grid, Typography } from '@mui/material';
import { useConfigurationLicence } from '../hooks';
import { FormModal } from 'components/molecules/Modal';
import { Resolver, useForm } from 'react-hook-form';
import {
  ILicenceTemplateApplicationProcessIntelligenceReport,
  ILicenceTemplateApplicationProcessIntelligenceReportForm,
} from 'models/licenceConfigurator';
import { yupResolver } from '@hookform/resolvers/yup';
import { colors } from 'theme/colors';
import { FormInput } from 'components/molecules/FormInput';
import useIntelligenceReportFormSchema from './schemaValidation';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { FormSelect } from 'components/molecules/FormSelect';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { useLang } from 'models/langContext';
import { EDictionaryTypes } from 'models/dictionary';

interface IInitialFormValues {
  sourceSystemUniqueId: string;
  instructions: string;
}

interface ILicenceTemplateApplicationProcessAddIntelligenceReport {
  handleClose: () => void;
  addSuccess: () => void;
  applicationProcessId: string;
  initialValues?: ILicenceTemplateApplicationProcessIntelligenceReport;
  intelligenceReportId?: string;
}

const initialFormValues: IInitialFormValues = {
  sourceSystemUniqueId: '',
  instructions: '',
};

const LicenceTemplateApplicationProcessAddIntelligenceReport = ({
  handleClose,
  addSuccess,
  applicationProcessId,
  initialValues,
  intelligenceReportId,
}: ILicenceTemplateApplicationProcessAddIntelligenceReport) => {
  const { t } = useTranslations();
  const { selectedLanguage } = useLang();
  const {
    addApplicationProccessIntelligenceReport,
    editApplicationProccessIntelligenceReport,
    loadDictionaries,
    intelligenceReportSourceSystem,
  } = useConfigurationLicence();
  const { schema } = useIntelligenceReportFormSchema();
  const { clearAlert, intelligenceReportAlert } = useAlert();

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([EDictionaryTypes.intelligenceReportSourceSystem]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    if (intelligenceReportId && initialValues) {
      reset({
        sourceSystemUniqueId: initialValues.sourceSystemUniqueId,
        instructions: initialValues.instructions,
      });
    }
  }, [intelligenceReportId, initialValues]);

  const close = () => {
    reset(initialFormValues);
    if (intelligenceReportAlert && intelligenceReportAlert.variant === EAlertVariants.error) {
      clearAlert(ActionTypes.CLEAR_INTELLIGENCE_REPORT_ALERT);
    }
    handleClose();
  };

  useEffect(
    () => {
      clearAlert(ActionTypes.CLEAR_INTELLIGENCE_REPORT_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<ILicenceTemplateApplicationProcessIntelligenceReportForm>({
    defaultValues: initialFormValues,
    resolver: yupResolver(
      schema,
    ) as unknown as Resolver<ILicenceTemplateApplicationProcessIntelligenceReportForm>,
  });

  const handleAdd = async (data: ILicenceTemplateApplicationProcessIntelligenceReportForm) => {
    let res = false;

    if (intelligenceReportId) {
      res = await editApplicationProccessIntelligenceReport(intelligenceReportId, {
        ...data,
      });
    } else {
      res = await addApplicationProccessIntelligenceReport({
        ...data,
        applicationProcessUniqueId: applicationProcessId,
      });
    }
    if (res) {
      addSuccess();
      close();
    }
  };

  return (
    <form onSubmit={handleSubmit(handleAdd)}>
      <FormModal
        open={true}
        onClickClose={() => {
          close();
          reset(initialFormValues);
        }}
        onClickApprove={handleSubmit(handleAdd)}
        approveButtonLabel={t('general.save.button')}
      >
        <Grid sx={{ gap: '4px' }}>
          <Grid sx={{ gap: 1.5, flexDirection: 'column', mb: 2.5 }}>
            <Typography variant="h5" sx={{ color: colors.primary500 }}>
              {!!initialValues
                ? t('licence.editIntelligenceReport.label')
                : t('licence.addIntelligenceReport.label')}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormSelect
              options={mapDictionaryToOptionProp(intelligenceReportSourceSystem)}
              setValue={setValue}
              name="sourceSystemUniqueId"
              label={t('licence.intelligenceReport.sourceSystem.label')}
              control={control}
              errors={errors}
              required
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormInput
              name="instructions"
              label={t('licence.intelligenceReport.instructions.label')}
              multiline
              setValue={setValue}
              minRows={6}
              control={control}
              errors={errors}
              required
              charsLimit={255}
              showCounter
            />
          </Grid>
          {intelligenceReportAlert &&
            intelligenceReportAlert.variant === EAlertVariants.error && (
              <Grid container size={{ xs: 12 }}>
                <Alert
                  text={intelligenceReportAlert.text}
                  variant={intelligenceReportAlert.variant}
                />
              </Grid>
            )}
        </Grid>
      </FormModal>
    </form>
  );
};

export { LicenceTemplateApplicationProcessAddIntelligenceReport };
